import PersonIcon from "@mui/icons-material/Person";
import {
  Box,
  Typography,
  CircularProgress,
  Alert
} from "@mui/material";

import { CustomCard } from "../../components/CustomCard";
import { useTheme } from "../../style/theme";
import ApexChart2 from "../../components/ui/ApexChart2";
import BarChart3 from "../../components/ui/BarChart3";
import {useTheme as themee} from "@mui/material";
import { useSubscriptionAnalytics, useSubscriptions } from "../../hooks/useSubscriptions";


const SubscriptionOverview = () => {
  const { data: analytics, isLoading: analyticsLoading, error: analyticsError } = useSubscriptionAnalytics();
  const { data: recentSubscriptions, isLoading: subscriptionsLoading, error: subscriptionsError } = useSubscriptions({
    page: 1,
    limit: 5,
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });

  const isLoading = analyticsLoading || subscriptionsLoading;
  const error = analyticsError || subscriptionsError;
  const { mode } = useTheme();
  const  theme  = themee();

  // Format analytics data for cards
  const cardData = analytics?.data ? [
    {
      title: "Total Subscriptions",
      value: analytics.data.totalSubscriptions?.toLocaleString() || "0",
      type: analytics.data.totalSubscriptionsGrowth >= 0 ? "increase" : "decrease",
      percentage: Math.abs(analytics.data.totalSubscriptionsGrowth || 0).toFixed(1),
    },
    {
      title: "Active Subscriptions",
      value: analytics.data.activeSubscriptions?.toLocaleString() || "0",
      type: analytics.data.activeSubscriptionsGrowth >= 0 ? "increase" : "decrease",
      percentage: Math.abs(analytics.data.activeSubscriptionsGrowth || 0).toFixed(1),
    },
    {
      title: "New Subscriptions",
      value: analytics.data.newSubscriptionsThisMonth?.toLocaleString() || "0",
      type: analytics.data.newSubscriptionsGrowth >= 0 ? "increase" : "decrease",
      percentage: Math.abs(analytics.data.newSubscriptionsGrowth || 0).toFixed(1),
    },
    {
      title: "Churn Rate",
      value: `${(analytics.data.churnRate || 0).toFixed(1)}%`,
      type: analytics.data.churnRateChange <= 0 ? "decrease" : "increase",
      percentage: Math.abs(analytics.data.churnRateChange || 0).toFixed(1),
    },
  ] : [];

  // Format recent subscriptions data
  const subscriptions = recentSubscriptions?.data?.slice(0, 5).map(sub => ({
    name: sub.user?.name || sub.userId || "Unknown User",
    date: new Date(sub.createdAt).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }),
    price: `$${(parseFloat(sub.amount) || 0).toFixed(2)}`,
    Icon: <PersonIcon />,
  })) || [];
  if (isLoading) {
    return (
      <Box sx={{ backgroundColor: "background.default" }} className="flex items-center justify-center h-96">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ backgroundColor: "background.default" }} className="p-4">
        <Alert severity="error">
          Failed to load subscription data: {error.response?.data?.message || error.message}
        </Alert>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ backgroundColor: "background.default" }}  className="flex flex-col gap-3">
        <Box>
          <div className="flex flex-row w-full">
            <div className="w-full">
              <Typography variant="h5" color="text.primary">
                <div className="mb-2">
                  <CustomCard
                    data={cardData}
                    mode={mode}
                    length={cardData.length}
                  />
                </div>
              </Typography>
            </div>
          </div>
        </Box>
        <Box className="h-full w-full grid grid-cols-1 md:grid-cols-1 lg:grid-cols-1 gap-6 mb-2">
          <Box
            className={`h-full w-full p-3  shadow-md rounded-xl border overflow-hidden ${mode === "dark"
              ? "border-[#292929] bg-neutral-1000"
              : "bg-neutral border-[#292929]"
              } `}
                 sx={{
                  boxShadow: 'none' ,
                  border: `1px solid ${theme.palette.divider}`,
                  padding: theme.spacing(2),
                  borderRadius: 2,
              }}
          >
            <Typography variant="h5" className="mb-2">
              Subscription Growth Graph
            </Typography>
            <ApexChart2 period="12m" />
          </Box>
        </Box>
        <Box className="h-full w-full grid grid-cols-1 md:grid-cols-4 lg:grid-cols-4 gap-6 mb-2">
          {/* First Box - 50% width on medium/large screens */}
          <Box
            className={`h-full col-span-1 md:col-span-3 p-3 shadow-md rounded-xl border overflow-hidden ${mode === "dark"
              ? "border-[#292929] bg-neutral-1000"
              : "bg-neutral border-[#292929]"
              }`}
               sx={{
                  boxShadow: 'none' ,
                  border: `1px solid ${theme.palette.divider}`,
                  padding: theme.spacing(2),
                  borderRadius: 2,
              }}
          >
            <Typography variant="h5" className="mb-2">
              Top 5 Plans
            </Typography>
            <BarChart3 limit={5} />
          </Box>

          {/* Third Box - 25% width on medium/large screens */}
          <Box
            className={`h-full p-3 rounded-xl border shadow-md overflow-hidden ${mode === "dark"
              ? "border-[#292929] bg-neutral-1000"
              : "bg-neutral border-[#292929]"
              }`}
          >
            <Typography variant="h5" className="mb-4">
              Recent Subscriptions
            </Typography>
            <div className="space-y-4">
              {subscriptions.map((subscription, index) => (
                <div
                  key={index}
                  className={`flex items-center justify-between p-3 rounded-lg 
    
                    transition-colors`}
                >
                  {/* Subscription Icon and Details */}
                  <div className="flex items-center space-x-4">
                    <div className={`w-12 h-12 flex items-center justify-center ${mode === "dark"
                      ? "bg-neutral-700"
                      : "bg-neutral-200"
                      } rounded-full`}>
                      {subscription.Icon} {/* Render the Icon component */}
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">
                        {subscription.name}
                      </h3>
                      <p className={`text-sm ${mode === "dark"
                        ? "text-neutral-400"
                        : "text-neutral-600"
                        }`}>
                        {subscription.date}
                      </p>
                    </div>
                  </div>

                  {/* Subscription Price */}
                  <div className="text-right">
                    <p className="font-bold text-lg">{subscription.price}</p>
                    <span className={`text-sm ${mode === "dark"
                      ? "text-neutral-400"
                      : "text-neutral-600"
                      }`}>/month</span>
                  </div>
                </div>
              ))}
            </div>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default SubscriptionOverview;